import {
  Component,
  lazy,
  Suspense,
  type ErrorInfo,
  type ReactNode,
} from 'react'

const ErrorBoundaryFallback = lazy(() =>
  import('@/components/layout/ErrorBoundaryFallback').then((mod) => ({
    default: mod.ErrorBoundaryFallback,
  })),
)

interface Props {
  children: ReactNode
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode)
  onReset?: () => void
  onError?: (error: Error, info: ErrorInfo) => void
  title?: string
  description?: string
  compact?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  public reset = () => {
    this.props.onReset?.()
    this.setState({ hasError: false, error: null })
  }

  public override render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    if (this.props.fallback) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(
          this.state.error ?? new Error('Unknown error'),
          this.reset,
        )
      }
      return this.props.fallback
    }

    const { compact, title, description } = this.props

    return (
      <Suspense
        fallback={
          <p role="alert" className="text-text p-6 text-center text-sm">
            Something went wrong
          </p>
        }
      >
        <ErrorBoundaryFallback
          error={this.state.error}
          reset={this.reset}
          compact={compact}
          title={title}
          description={description}
        />
      </Suspense>
    )
  }
}
